// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Grid,
  Icon,
  Label,
  Button,
  Message,
  Breadcrumb,
  Statistic,
  Card,
  List,
  Tab,
  Popup,
  Dropdown,
  Loader,
} from 'semantic-ui-react';
import { supportStaffApi } from '../api/supportStaffApi';
import type {
  SupportStaffDetail,
  AssignedAdvisor,
  SupportActivityLog,
  AssignmentHistoryItem,
  PageResponse,
} from '../types/supportStaff';
import AssignmentManagementModal from '../components/AssignmentManagementModal';
import EditSupportStaffModal from '../components/EditSupportStaffModal';
import { formatDateLong, formatRelativeTime } from '@loa/theme';

function SupportStaffDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [supportStaff, setSupportStaff] = useState<SupportStaffDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Activity log state
  const [activityLog, setActivityLog] = useState<SupportActivityLog[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Assignment history state
  const [assignmentHistory, setAssignmentHistory] = useState<AssignmentHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadSupportStaff = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch both support staff details and assigned advisors in parallel
      const [data, assignedAdvisors] = await Promise.all([
        supportStaffApi.getById(id),
        supportStaffApi.getAssignedAdvisors(id),
      ]);

      // Merge assigned advisors into support staff data
      // Transform AdvisorForAssignment to AssignedAdvisor format
      const assignedAdvisorsList: AssignedAdvisor[] = assignedAdvisors.map((advisor) => ({
        id: advisor.id,
        firstName: advisor.firstName,
        lastName: advisor.lastName,
        email: advisor.email,
        photoUrl: advisor.photoUrl,
        profileCount: advisor.profileCount,
        assignedAt: '', // Not available from this endpoint
      }));

      setSupportStaff({
        ...data,
        assignedAdvisors: assignedAdvisorsList,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load support staff');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadActivityLog = useCallback(async () => {
    if (!id) return;

    setActivityLoading(true);
    try {
      const response: PageResponse<SupportActivityLog> = await supportStaffApi.getActivityLog(
        id,
        0,
        10
      );
      setActivityLog(response.content);
    } catch (err) {
      console.error('Failed to load activity log:', err);
    } finally {
      setActivityLoading(false);
    }
  }, [id]);

  const loadAssignmentHistory = useCallback(async () => {
    if (!id) return;

    setHistoryLoading(true);
    try {
      const response: PageResponse<AssignmentHistoryItem> =
        await supportStaffApi.getAssignmentHistory(id, 0, 20);
      setAssignmentHistory(response.content);
    } catch (err) {
      console.error('Failed to load assignment history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadSupportStaff();
  }, [loadSupportStaff]);

  useEffect(() => {
    if (supportStaff) {
      loadActivityLog();
      loadAssignmentHistory();
    }
  }, [supportStaff, loadActivityLog, loadAssignmentHistory]);

  // Handle tab query param
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'assignments') {
      setActiveTab(1);
      setAssignmentModalOpen(true);
    }
  }, [searchParams]);

  // Handle /edit URL path - open edit modal automatically
  useEffect(() => {
    if (location.pathname.endsWith('/edit') && supportStaff) {
      setEditModalOpen(true);
    }
  }, [location.pathname, supportStaff]);

  const handleAssignmentSuccess = () => {
    setAssignmentModalOpen(false);
    loadSupportStaff();
    loadAssignmentHistory();
  };

  const handleRemoveAdvisor = async (advisorId: string) => {
    if (!id || !supportStaff) return;

    try {
      await supportStaffApi.unassignAdvisor(id, supportStaff.brokerageId, advisorId);
      await loadSupportStaff();
      await loadAssignmentHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove assignment');
    }
  };

  const handleEditSuccess = () => {
    setEditModalOpen(false);
    loadSupportStaff();
    // Navigate back to detail page if we're on the /edit URL
    if (location.pathname.endsWith('/edit')) {
      navigate(`/support-staff/${id}`, { replace: true });
    }
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
    // Navigate back to detail page if we're on the /edit URL
    if (location.pathname.endsWith('/edit')) {
      navigate(`/support-staff/${id}`, { replace: true });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'grey';
      case 'invited':
        return 'blue';
      default:
        return 'grey';
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (loading) {
    return (
      <Container style={{ marginTop: '2rem', textAlign: 'center' }}>
        <Loader active inline="centered" size="large">
          Loading support staff...
        </Loader>
      </Container>
    );
  }

  // Only a FAILED LOAD gets the full-page error: loadSupportStaff leaves
  // supportStaff null on failure, so this still covers it (and still shows the
  // message, via `error ||` below). Including `error` here also swallowed
  // errors raised by the action handlers AFTER a successful load — those
  // replaced the whole page instead of reaching the dismissible inline banner
  // further down, which was therefore unreachable.
  if (!supportStaff) {
    return (
      <Container style={{ marginTop: '2rem' }}>
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error || 'Support staff not found'}</p>
        </Message>
        <Button onClick={() => navigate('/support-staff')}>
          <Icon name="arrow left" /> Back to Support Staff
        </Button>
      </Container>
    );
  }

  const totalProfileAccess =
    supportStaff.assignedAdvisors?.reduce((sum, a) => sum + a.profileCount, 0) ?? 0;

  const renderRecentActivity = () => {
    if (activityLoading) {
      return <Loader active inline="centered" size="small" />;
    }
    if (activityLog.length === 0) {
      return (
        <Message info size="small">
          <Message.Content>No recent activity</Message.Content>
        </Message>
      );
    }
    return (
      <List divided relaxed>
        {activityLog.slice(0, 5).map((activity) => (
          <List.Item key={activity.id}>
            <List.Icon name="circle" size="tiny" color="blue" />
            <List.Content>
              <List.Header>{activity.title}</List.Header>
              <List.Description>
                {activity.subtitle}
                <br />
                <small style={{ color: '#999' }}>
                  {formatRelativeTime(activity.timestamp)}
                </small>
              </List.Description>
            </List.Content>
          </List.Item>
        ))}
      </List>
    );
  };

  const panes = [
    {
      menuItem: { key: 'overview', icon: 'info circle', content: 'Overview' },
      render: () => (
        <Tab.Pane attached={false}>
          <Grid stackable>
            <Grid.Row>
              <Grid.Column width={10}>
                {/* Personal Information */}
                <Segment>
                  <Header as="h4">
                    <Icon name="user" />
                    Personal Information
                  </Header>
                  <List divided relaxed>
                    <List.Item>
                      <List.Icon name="mail" />
                      <List.Content>
                        <List.Header>Email</List.Header>
                        <List.Description>
                          {supportStaff.email}
                          <Popup
                            content="Copied!"
                            on="click"
                            pinned
                            trigger={
                              <Button
                                icon="copy"
                                size="mini"
                                basic
                                style={{ marginLeft: '0.5em' }}
                                onClick={() =>
                                  navigator.clipboard.writeText(supportStaff.email)
                                }
                              />
                            }
                          />
                        </List.Description>
                      </List.Content>
                    </List.Item>
                    <List.Item>
                      <List.Icon name="phone" />
                      <List.Content>
                        <List.Header>Phone</List.Header>
                        <List.Description>
                          {supportStaff.phoneNumber || 'Not provided'}
                        </List.Description>
                      </List.Content>
                    </List.Item>
                    <List.Item>
                      <List.Icon name="building" />
                      <List.Content>
                        <List.Header>Brokerage</List.Header>
                        <List.Description>
                          {supportStaff.brokerageName || 'Unknown'}
                        </List.Description>
                      </List.Content>
                    </List.Item>
                    <List.Item>
                      <List.Icon name="calendar" />
                      <List.Content>
                        <List.Header>Joined</List.Header>
                        <List.Description>
                          {formatDateLong(supportStaff.createdAt)}
                        </List.Description>
                      </List.Content>
                    </List.Item>
                    <List.Item>
                      <List.Icon name="clock" />
                      <List.Content>
                        <List.Header>Last Active</List.Header>
                        <List.Description>
                          {formatRelativeTime(supportStaff.lastLoginAt)}
                        </List.Description>
                      </List.Content>
                    </List.Item>
                    {supportStaff.notes && (
                      <List.Item>
                        <List.Icon name="sticky note" />
                        <List.Content>
                          <List.Header>Notes</List.Header>
                          <List.Description>{supportStaff.notes}</List.Description>
                        </List.Content>
                      </List.Item>
                    )}
                  </List>
                </Segment>

                {/* Assigned Advisors */}
                <Segment>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1em',
                    }}
                  >
                    <Header as="h4" style={{ margin: 0 }}>
                      <Icon name="users" />
                      Assigned Advisors ({supportStaff.assignedAdvisors?.length ?? 0})
                    </Header>
                    <Button
                      primary
                      size="small"
                      onClick={() => setAssignmentModalOpen(true)}
                    >
                      <Icon name="exchange" /> Manage Assignments
                    </Button>
                  </div>

                  {supportStaff.assignedAdvisors?.length === 0 ? (
                    <Message info>
                      <Message.Content>
                        No advisors assigned yet. Click "Manage Assignments" to assign
                        advisors.
                      </Message.Content>
                    </Message>
                  ) : (
                    <Card.Group>
                      {supportStaff.assignedAdvisors?.map((advisor: AssignedAdvisor) => (
                        <Card key={advisor.id} fluid>
                          <Card.Content>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
                              <div
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  backgroundColor: '#2185D0',
                                  color: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 'bold',
                                }}
                              >
                                {advisor.photoUrl ? (
                                  <img
                                    src={advisor.photoUrl}
                                    alt={`${advisor.firstName} ${advisor.lastName}`}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      borderRadius: '50%',
                                      objectFit: 'cover',
                                    }}
                                  />
                                ) : (
                                  getInitials(advisor.firstName, advisor.lastName)
                                )}
                              </div>
                              <div style={{ flex: 1 }}>
                                <Card.Header>
                                  {advisor.firstName} {advisor.lastName}
                                </Card.Header>
                                <Card.Meta>{advisor.email}</Card.Meta>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <Label size="small">
                                  {advisor.profileCount} profile
                                  {advisor.profileCount === 1 ? '' : 's'}
                                </Label>
                                <div style={{ marginTop: '0.5em' }}>
                                  <small style={{ color: '#999' }}>
                                    Assigned {formatDateLong(advisor.assignedAt)}
                                  </small>
                                </div>
                              </div>
                              <Dropdown
                                icon="ellipsis vertical"
                                floating
                                button
                                className="icon mini"
                                direction="left"
                              >
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    icon="eye"
                                    text="View Advisor"
                                    onClick={() => navigate(`/advisors/${advisor.id}/edit`)}
                                  />
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    icon="user times"
                                    text="Remove Assignment"
                                    onClick={() => handleRemoveAdvisor(advisor.id)}
                                    style={{ color: 'red' }}
                                  />
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </Card.Content>
                        </Card>
                      ))}
                    </Card.Group>
                  )}
                </Segment>
              </Grid.Column>

              <Grid.Column width={6}>
                {/* Quick Stats */}
                <Segment>
                  <Header as="h4">
                    <Icon name="chart bar" />
                    Quick Stats
                  </Header>
                  <Statistic.Group size="mini" widths={2}>
                    <Statistic>
                      <Statistic.Value>
                        {supportStaff.assignedAdvisors?.length ?? 0}
                      </Statistic.Value>
                      <Statistic.Label>Assigned Advisors</Statistic.Label>
                    </Statistic>
                    <Statistic>
                      <Statistic.Value>{totalProfileAccess}</Statistic.Value>
                      <Statistic.Label>Total Profile Access</Statistic.Label>
                    </Statistic>
                    <Statistic>
                      <Statistic.Value>{supportStaff.actionsThisWeek ?? 0}</Statistic.Value>
                      <Statistic.Label>Actions This Week</Statistic.Label>
                    </Statistic>
                    <Statistic>
                      <Statistic.Value>
                        {formatRelativeTime(supportStaff.lastLoginAt)}
                      </Statistic.Value>
                      <Statistic.Label>Last Active</Statistic.Label>
                    </Statistic>
                  </Statistic.Group>
                </Segment>

                {/* Recent Activity */}
                <Segment>
                  <Header as="h4">
                    <Icon name="history" />
                    Recent Activity
                  </Header>
                  {renderRecentActivity()}
                </Segment>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Tab.Pane>
      ),
    },
    {
      menuItem: { key: 'history', icon: 'history', content: 'Assignment History' },
      render: () => (
        <Tab.Pane attached={false}>
          <Segment>
            {historyLoading && <Loader active inline="centered" />}
            {!historyLoading && assignmentHistory.length === 0 && (
              <Message info>
                <Message.Content>No assignment history yet.</Message.Content>
              </Message>
            )}
            {!historyLoading && assignmentHistory.length > 0 && (
              <List divided relaxed>
                {assignmentHistory.map((item) => (
                  <List.Item key={item.id}>
                    <List.Icon
                      name={item.type === 'ASSIGNED' ? 'user plus' : 'user times'}
                      color={item.type === 'ASSIGNED' ? 'green' : 'red'}
                      size="large"
                    />
                    <List.Content>
                      <List.Header>
                        {item.type === 'ASSIGNED' ? 'Assigned to' : 'Unassigned from'}{' '}
                        {item.advisorName}
                      </List.Header>
                      <List.Description>
                        By {item.performedByName} on {formatDateLong(item.timestamp)}
                        {item.reason && <div>Reason: {item.reason}</div>}
                      </List.Description>
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            )}
          </Segment>
        </Tab.Pane>
      ),
    },
  ];

  return (
    <Container style={{ marginTop: '2rem' }}>
      <Breadcrumb>
        <Breadcrumb.Section link onClick={() => navigate('/brokerages')}>
          Admin
        </Breadcrumb.Section>
        <Breadcrumb.Divider />
        <Breadcrumb.Section link onClick={() => navigate('/support-staff')}>
          Support Staff
        </Breadcrumb.Section>
        <Breadcrumb.Divider />
        <Breadcrumb.Section active>
          {supportStaff.firstName} {supportStaff.lastName}
        </Breadcrumb.Section>
      </Breadcrumb>

      {/* Header Section */}
      <Segment style={{ marginTop: '1em' }}>
        <Grid>
          <Grid.Row verticalAlign="middle">
            <Grid.Column width={1}>
              <div
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: '#00B5AD',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5em',
                  fontWeight: 'bold',
                }}
              >
                {getInitials(supportStaff.firstName, supportStaff.lastName)}
              </div>
            </Grid.Column>
            <Grid.Column width={11}>
              <Header as="h2" style={{ marginBottom: '0.25em' }}>
                {supportStaff.firstName} {supportStaff.lastName}
                <Label
                  color={getStatusColor(supportStaff.status)}
                  style={{ marginLeft: '1em' }}
                >
                  {supportStaff.status.toUpperCase()}
                </Label>
                <Label color="teal" style={{ marginLeft: '0.5em' }}>
                  <Icon name="headphones" />
                  SUPPORT
                </Label>
              </Header>
              <p style={{ color: '#666', margin: 0 }}>
                {supportStaff.email} | {supportStaff.brokerageName}
              </p>
            </Grid.Column>
            <Grid.Column width={4} textAlign="right">
              <Button
                primary
                onClick={() => setEditModalOpen(true)}
              >
                <Icon name="edit" /> Edit
              </Button>
              <Dropdown
                icon="ellipsis vertical"
                floating
                button
                className="icon"
                direction="left"
                style={{ marginLeft: '0.5em' }}
              >
                <Dropdown.Menu>
                  <Dropdown.Item
                    icon="exchange"
                    text="Manage Assignments"
                    onClick={() => setAssignmentModalOpen(true)}
                  />
                  <Dropdown.Divider />
                  {supportStaff.status === 'active' && (
                    <Dropdown.Item
                      icon="user times"
                      text="Deactivate"
                      onClick={async () => {
                        try {
                          await supportStaffApi.deactivate(supportStaff.id);
                          await loadSupportStaff();
                        } catch (err) {
                          setError(
                            err instanceof Error ? err.message : 'Failed to deactivate'
                          );
                        }
                      }}
                      style={{ color: 'red' }}
                    />
                  )}
                  {supportStaff.status === 'inactive' && (
                    <Dropdown.Item
                      icon="user plus"
                      text="Reactivate"
                      onClick={async () => {
                        try {
                          await supportStaffApi.reactivate(supportStaff.id);
                          await loadSupportStaff();
                        } catch (err) {
                          setError(
                            err instanceof Error ? err.message : 'Failed to reactivate'
                          );
                        }
                      }}
                      style={{ color: 'green' }}
                    />
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>

      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {/* Tabs */}
      <Tab
        menu={{ secondary: true, pointing: true }}
        panes={panes}
        activeIndex={activeTab}
        onTabChange={(_, data) => setActiveTab(data.activeIndex as number)}
      />

      {/* Assignment Management Modal.
          No `supportStaff &&` guard: the early return above already narrowed it
          to non-null, so the guard was dead. Both modals are controlled by their
          own `open` prop. */}
      <AssignmentManagementModal
        open={assignmentModalOpen}
        supportStaffId={supportStaff.id}
        supportStaffName={`${supportStaff.firstName} ${supportStaff.lastName}`}
        brokerageId={supportStaff.brokerageId}
        onClose={() => setAssignmentModalOpen(false)}
        onSuccess={handleAssignmentSuccess}
      />

      {/* Edit Support Staff Modal */}
      <EditSupportStaffModal
        open={editModalOpen}
        supportStaff={supportStaff}
        onClose={handleEditClose}
        onSuccess={handleEditSuccess}
      />

    </Container>
  );
}

export default SupportStaffDetailPage;
